import { FC } from 'react';
import Typography from '@components/common/typography';
import VerificationCode from '@components/verification_code';
import styles from './styles.module.scss';
import { apiAuthService, apiUserService } from '@api';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { useLocales } from '@hooks/helpers/useLocales';
import { useTranslation } from 'react-i18next';
import { IModalComponentProps } from '@context/modal_context';
import { useUserContext } from '@hooks/context/useUserContext';
import { useApiQuery } from '@hooks/api/useApiQuery';

const VerificationEmailModal: FC<
    IModalComponentProps<{
        code: string;
        emailId: string;
        email: string;
        secure_id?: string;
        is_primary?: boolean;
        customCode?: boolean;
        is_primary_set?: boolean;
    }>
> = ({ modalData, changeModalData }) => {
    const { t } = useTranslation('modals', {
        keyPrefix: 'verification_email'
    });
    const { requestSuccessLocale, requestErrorLocale } = useLocales();
    const { user } = useUserContext();

    const { handleRequest: handleResendSecurity } = useApiMutation({
        method: () => apiAuthService.sendSecurityCode(),
        onSuccess: (secure_id) => {
            requestSuccessLocale('resent_email_verification');
            changeModalData({ ...modalData, secure_id, code: '' });
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleResend } = useApiMutation({
        method: () => apiUserService.sendEmailVerificationCode(modalData?.emailId as string),
        onSuccess: () => {
            requestSuccessLocale('resent_email_verification');
            changeModalData({ ...modalData, code: '' });
        },
        onError: requestErrorLocale
    });

    useApiQuery({
        method: () => {
            if (modalData?.is_primary) {
                return apiAuthService.sendSecurityCode();
            }
            return Promise.reject();
        },
        deps: [modalData?.emailId, modalData?.is_primary],
        isInitialRequest: false,
        condition: !!modalData?.emailId && modalData?.is_primary && !modalData?.customCode,
        onSuccess: (secure_id) => {
            changeModalData({ ...modalData, secure_id });
        }
    });

    useApiQuery({
        method: () => {
            if (!modalData?.is_primary) {
                return apiUserService.sendEmailVerificationCode(modalData?.emailId as string);
            }
            return Promise.reject();
        },
        deps: [modalData?.emailId, modalData?.is_primary],
        isInitialRequest: false,
        condition: !!modalData?.emailId && !modalData?.is_primary
    });

    return (
        <div className={styles.wrapper}>
            <Typography variant="h4" alignment="center" className={styles.title}>
                {modalData?.is_primary_set ? 'We just sen you a SMS' : t('title')}
            </Typography>
            <Typography variant="body3" alignment="center" className={styles.description}>
                {t('to')}{' '}
                <b>
                    {modalData?.is_primary_set
                        ? 'you primary phone'
                        : modalData?.is_primary
                          ? 'your primary email'
                          : modalData?.email || user?.email}
                </b>
            </Typography>
            <div className={styles.content}>
                {!modalData?.customCode && (
                    <div>
                        <Typography variant="body2" weight="medium" alignment="center">
                            {t('code_title')}
                        </Typography>
                        <button
                            onClick={() => {
                                if (modalData?.is_primary) {
                                    handleResendSecurity(undefined);
                                } else {
                                    handleResend(undefined);
                                }
                            }}>
                            {t('resend')}
                        </button>
                    </div>
                )}
                <VerificationCode
                    onChange={(value) => changeModalData({ ...modalData, code: value })}
                    value={modalData?.code}
                />
            </div>
        </div>
    );
};

export default VerificationEmailModal;
